import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog } from "primereact/dialog";
import AjusteComissaoService from "../../../services/resource/AjusteComissaoService";
import TabelaAjusteComissao from "./TabelaAjusteComissao";
import CadastrarAjusteComissao from "./CadastrarAjuste";
import Button from "../../../componentes/button/button";
import AjusteRetornos from "./AjusteRetornos";
import { ANOS, MESES } from "../../../utils/options";

function StatCard({ label, value, icon, color, delay = 0, onClick, active }) {
    const colorMap = {
        amber: { bg: "bg-amber-50", border: "border-amber-300", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" },
        green: { bg: "bg-green-50", border: "border-green-300", icon: "bg-green-100 text-green-600", value: "text-green-700" },
        red: { bg: "bg-red-50", border: "border-red-300", icon: "bg-red-100 text-red-600", value: "text-red-700" },
        blue: { bg: "bg-blue-50", border: "border-blue-300", icon: "bg-blue-100 text-blue-600", value: "text-blue-700" },
    };
    const c = colorMap[color] ?? colorMap.blue;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay }}
            className={`flex items-center gap-4 rounded-xl ${active ? 'border-2' : 'border'} ${c.border}  ${c.bg} px-5 py-4 shadow-card cursor-pointer transition-all duration-150`}
            onClick={onClick}
        >
            <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-lg ${c.icon}`}>
                <i className={icon} />
            </div>
            <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
                <p className={`text-2xl font-bold font-display leading-tight ${c.value}`}>{value}</p>
            </div>
        </motion.div>
    );
}

function Item({ label, value }) {
    return (
        <div className="flex justify-between border-b border-border pb-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mr-3">{label}</span>
            <span className="text-sm font-semibold text-foreground">{value}</span>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        PENDENTE: { label: "Pendente", cls: "bg-amber-100 text-amber-700 border-amber-200" },
        APROVADO: { label: "Aprovado", cls: "bg-success/10 text-success border-success/30" },
        REJEITADO: { label: "Rejeitado", cls: "bg-destructive/10 text-destructive border-destructive/30" },
    };
    const s = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground border-border" };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-0.5 text-xs font-semibold ${s.cls}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
            {s.label}
        </span>
    );
}

export default function AjustesComissao() {
    const ajusteService = new AjusteComissaoService();

    const now = new Date();
    const [ajustes, setAjustes] = useState([]);
    const [selected, setSelected] = useState(null);
    const [modalAjuste, setModalAjuste] = useState(false);
    const [modalRetornos, setModalRetornos] = useState(false);
    const [loading, setLoading] = useState(true);
    const [modalView, setModalView] = useState(false);
    const [mes, setMes] = useState(now.getMonth() + 1);
    const [ano, setAno] = useState(now.getFullYear());
    const [filtro, setFiltro] = useState("PENDENTE");

    const carregar = () => {
        setLoading(true);
        ajusteService.listar({ mes, ano })
            .then(res => setAjustes(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => { carregar(); }, [mes, ano]);

    const ajustesFiltrados = useMemo(() => {
        if (filtro === "TODOS") return ajustes;
        return ajustes.filter(a => a.status === filtro);
    }, [ajustes, filtro]);

    const stats = useMemo(() => {
        const total = ajustes.length;
        const pendentes = ajustes.filter(a => a.status === "PENDENTE").length;
        const aprovados = ajustes.filter(a => a.status === "APROVADO").length;
        const rejeitados = ajustes.filter(a => a.status === "REJEITADO").length;
        return { total, pendentes, aprovados, rejeitados };
    }, [ajustes]);

    const aprovar = () => {
        ajusteService.aprovar(selected.id).then(() => { setModalView(false); carregar(); });
    };

    const rejeitar = () => {
        ajusteService.rejeitar(selected.id).then(() => { setModalView(false); carregar(); });
    };

    const onRowClick = (row) => { setSelected(row); setModalView(true); };

    return (
        <div className="min-h-screen bg-background px-4 py-6">
            <div className="max-w-[1600px] mx-auto space-y-6">

                {/* ── Header ── */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-wrap items-end justify-between gap-4"
                >
                    <div>
                        <h1 className="text-2xl font-bold font-display text-foreground tracking-tight">
                            Ajustes de Comissão
                        </h1>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            Solicitações de ajuste de cálculo de comissão
                        </p>
                    </div>

                    {/* ── Filters ── */}
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">
                        <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                        <select
                            value={mes}
                            onChange={e => setMes(e.target.value)}
                            className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                        <span className="text-muted-foreground">|</span>
                        <select
                            value={ano}
                            onChange={e => setAno(Number(e.target.value))}
                            className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                        >
                            {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}

                        </select>
                    </div>
                </motion.div>

                {/* ── Stat Cards ── */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <StatCard active={filtro === "TODOS"} onClick={() => setFiltro("TODOS")} label="Total" value={stats.total} icon="fa fa-layer-group" color="blue" delay={0.05} />
                    <StatCard active={filtro === "PENDENTE"} onClick={() => setFiltro("PENDENTE")} label="Pendentes" value={stats.pendentes} icon="fa fa-clock" color="amber" delay={0.10} />
                    <StatCard active={filtro === "APROVADO"} onClick={() => setFiltro("APROVADO")} label="Aprovados" value={stats.aprovados} icon="fa fa-check-circle" color="green" delay={0.15} />
                    <StatCard active={filtro === "REJEITADO"} onClick={() => setFiltro("REJEITADO")} label="Rejeitados" value={stats.rejeitados} icon="fa fa-times-circle" color="red" delay={0.20} />
                </div>

                {/* ── Table ── */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.25 }}
                >
                    <TabelaAjusteComissao
                        data={ajustesFiltrados}
                        loading={loading}
                        onRowClick={onRowClick}
                        onRetornos={() => setModalRetornos(true)}
                        onCreate={() => setModalAjuste(true)}
                    />
                </motion.div>
            </div>

            {/* ── Detail Modal ── */}
            <Dialog
                blockScroll
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-file-alt text-primary" />
                        Detalhes do Ajuste
                    </div>
                }
                visible={modalView}
                modal
                onHide={() => setModalView(false)}
                pt={{ content: { className: "pt-2 pb-4 px-5" } }}
            >
                <AnimatePresence>
                    {selected && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="space-y-3 text-sm"
                        >
                            <div className="flex justify-center py-2">
                                <StatusBadge status={selected.status} />
                            </div>

                            <div className="space-y-2.5 rounded-lg bg-muted border border-border p-4">
                                <Item label="Filial" value={selected.filialNome} />
                                <Item label="Transporte" value={selected.transporte} />
                                <Item label="Tipo Ajuste" value={selected.tipo} />
                                <Item label="Motivo" value={selected.motivo} />
                                <Item label="Solicitado por" value={selected.solicitadoPor} />
                                <Item label="Data Solicitação"
                                    value={new Date(selected.dataSolicitacao).toLocaleString("pt-BR")} />

                                {selected.aprovadoPor && (
                                    <>
                                        <Item label="Aprovado por" value={selected.aprovadoPor} />
                                        <Item label="Data Aprovação"
                                            value={new Date(selected.dataAprovacao).toLocaleString("pt-BR")} />
                                    </>
                                )}
                            </div>

                            <div className="flex gap-2 justify-end pt-1">
                                <Button
                                    label="Reprovar"
                                    variant="red"
                                    disabled={selected.status === "REJEITADO"}
                                    iconLeft="fa fa-times"
                                    onClick={rejeitar}
                                />
                                <Button
                                    variant="green"
                                    iconLeft="fa fa-check"
                                    disabled={selected.status === "APROVADO"}
                                    onClick={aprovar}
                                    label="Aprovar"
                                />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Dialog>

            {/* ── Create Modal ── */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-plus-circle text-primary" />
                        Solicitar Ajuste
                    </div>
                }
                blockScroll
                visible={modalAjuste}
                onHide={() => setModalAjuste(false)}
                className="w-[80%]"
            >
                <CadastrarAjusteComissao
                    ano={ano}
                    mes={mes}
                    onClose={() => setModalAjuste(false)}
                    onSuccess={carregar}
                />
            </Dialog>

            <Dialog
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-plus-circle text-primary" />
                        Solicitar Ajuste
                    </div>
                }
                blockScroll
                visible={modalRetornos}
                onHide={() => setModalRetornos(false)}
                className="w-[80%]"
            >
                <AjusteRetornos
                    onClose={() => setModalRetornos(false)}
                    onSuccess={carregar}
                />
            </Dialog>
        </div>
    );
}
